import { Suspense, forwardRef, useCallback, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useSuspenseQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { RiPlayFill, RiMusic2Line, RiDraggable } from '@remixicon/react'
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	closestCenter,
	type DragStartEvent,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	SortableContext,
	rectSortingStrategy,
	useSortable,
	arrayMove,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Skeleton } from '@/components/ui/skeleton'
import { playlistsQueryOptions, reorderPlaylistsMutationOptions } from '@/lib/queries/playlists'
import { playMutationOptions } from '@/lib/queries/playback'
import { getPlaylistColor } from '@/lib/playlist-colors'

function PlaylistGridSkeleton() {
	return (
		<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-4 rounded-lg border border-border/50 bg-card/30 p-4"
				>
					<Skeleton className="size-11 shrink-0 rounded-md" />
					<div className="min-w-0 flex-1 space-y-2">
						<Skeleton className="h-3.5 w-24" />
						<Skeleton className="h-3 w-16" />
					</div>
				</div>
			))}
		</div>
	)
}

type Playlist = { id: string; name: string; icon: string | null; color: string | null; songCount: number }

const PlaylistCard = forwardRef<HTMLButtonElement, {
	playlist: Playlist
	style?: React.CSSProperties
	isDragOverlay?: boolean
	isPlaceholder?: boolean
	onPlay?: () => void
} & React.ButtonHTMLAttributes<HTMLButtonElement>>(
	({ playlist, style, isDragOverlay, isPlaceholder, onPlay, ...props }, ref) => (
		<button
			ref={ref}
			type="button"
			style={{
				...style,
				...(playlist.color && {
					'--playlist-accent': `color-mix(in oklch, ${getPlaylistColor(playlist.color)} 25%, transparent)`,
				}),
			} as React.CSSProperties}
			className={[
				'group relative flex items-center gap-4 rounded-lg border p-4 text-left',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
				isPlaceholder
					? 'border-primary/20 bg-primary/[0.03]'
					: isDragOverlay
						? 'border-primary/40 bg-card shadow-xl shadow-primary/5 ring-1 ring-primary/10'
						: playlist.color
							? 'border-border/50 bg-card/30 transition-colors hover:border-[var(--playlist-accent)] hover:bg-card/60'
							: 'border-border/50 bg-card/30 transition-colors hover:border-primary/20 hover:bg-card/60',
			].join(' ')}
			{...props}
		>
			{/* Icon */}
			<div
				className={[
					'relative flex size-11 shrink-0 items-center justify-center rounded-md',
					playlist.color
						? ''
						: isDragOverlay ? 'bg-primary/15' : 'bg-primary/10 group-hover:bg-primary/15',
					onPlay ? 'cursor-pointer play-icon-hover' : '',
				].join(' ')}
				style={{
					...(playlist.color ? {
						backgroundColor: `color-mix(in oklch, ${getPlaylistColor(playlist.color)} 12%, transparent)`,
					} : undefined),
					...(onPlay ? {
						'--play-hover-bg': playlist.color
							? `color-mix(in oklch, ${getPlaylistColor(playlist.color)} 25%, transparent)`
							: 'oklch(0.77 0.16 70 / 20%)',
						'--play-hover-glow': playlist.color
							? `color-mix(in oklch, ${getPlaylistColor(playlist.color)} 20%, transparent)`
							: 'oklch(0.77 0.16 70 / 12%)',
					} : undefined),
				} as React.CSSProperties}
				onClick={onPlay ? (e) => {
					e.stopPropagation()
					onPlay()
				} : undefined}
				onPointerDown={onPlay ? (e) => e.stopPropagation() : undefined}
			>
				{playlist.icon ? (
					<span className="text-lg leading-none transition-all group-hover:scale-0 group-hover:opacity-0">{playlist.icon}</span>
				) : (
					<RiMusic2Line
						className="size-[18px] text-primary transition-all group-hover:scale-0 group-hover:opacity-0"
						style={playlist.color ? { color: getPlaylistColor(playlist.color) } : undefined}
					/>
				)}
				<RiPlayFill
					className="absolute size-[18px] scale-0 text-primary opacity-0 transition-all group-hover:scale-100 group-hover:opacity-100"
					style={playlist.color ? { color: getPlaylistColor(playlist.color) } : undefined}
				/>
			</div>

			{/* Info */}
			<div className="relative min-w-0 flex-1">
				<h3 className="truncate text-[13px] font-medium leading-tight">{playlist.name}</h3>
				<p className="mt-0.5 text-[11px] text-muted-foreground">
					{playlist.songCount} {playlist.songCount === 1 ? 'song' : 'songs'}
				</p>
			</div>

			{/* Drag handle hint */}
			{!isDragOverlay && (
				<RiDraggable className="size-4 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/40" />
			)}
		</button>
	)
)

function SortablePlaylistCard({ playlist }: { playlist: Playlist }) {
	const navigate = useNavigate()
	const playMutation = useMutation(playMutationOptions())
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: playlist.id })

	const style = {
		transform: CSS.Translate.toString(transform),
		transition: isDragging ? undefined : transition,
	}

	if (isDragging) {
		return (
			<PlaylistCard
				ref={setNodeRef}
				style={style}
				playlist={playlist}
				isPlaceholder
				data-playlist-id={playlist.id}
				{...attributes}
				{...listeners}
			/>
		)
	}

	return (
		<PlaylistCard
			ref={setNodeRef}
			style={style}
			playlist={playlist}
			data-playlist-id={playlist.id}
			onClick={() => navigate({ to: '/playlists/$id', params: { id: playlist.id } })}
			onPlay={playlist.songCount > 0 ? () => playMutation.mutate({ playlistId: playlist.id }) : undefined}
			{...attributes}
			{...listeners}
		/>
	)
}

function PlaylistGridContent() {
	const { data: playlists } = useSuspenseQuery(playlistsQueryOptions)
	const queryClient = useQueryClient()
	const [activeId, setActiveId] = useState<string | null>(null)
	const [activeWidth, setActiveWidth] = useState<number | null>(null)

	const reorderMutation = useMutation(reorderPlaylistsMutationOptions(queryClient))

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragStart = useCallback((event: DragStartEvent) => {
		const id = event.active.id as string
		setActiveId(id)
		const node = document.querySelector(`[data-playlist-id="${id}"]`) as HTMLElement | null
		setActiveWidth(node?.getBoundingClientRect().width ?? null)
	}, [])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) {
				setActiveId(null)
				return
			}

			const oldIndex = playlists.findIndex((p) => p.id === active.id)
			const newIndex = playlists.findIndex((p) => p.id === over.id)
			if (oldIndex === -1 || newIndex === -1) {
				setActiveId(null)
				return
			}

			const reordered = arrayMove(playlists, oldIndex, newIndex)

			// Synchronous update — batched with setActiveId in the same render
			queryClient.setQueryData(playlistsQueryOptions.queryKey, reordered)
			setActiveId(null)

			reorderMutation.mutate(reordered.map((p) => p.id))
		},
		[playlists, queryClient, reorderMutation]
	)

	const handleDragCancel = useCallback(() => {
		setActiveId(null)
	}, [])

	const activePlaylist = activeId ? playlists.find((p) => p.id === activeId) : null

	if (playlists.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/50 bg-card/20 py-16">
				<RiMusic2Line className="size-8 text-muted-foreground/30" />
				<p className="mt-4 text-sm text-muted-foreground/70">No playlists yet</p>
				<p className="mt-1 text-[11px] text-muted-foreground/40">
					Create a playlist to get started
				</p>
			</div>
		)
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
		>
			<SortableContext items={playlists.map((p) => p.id)} strategy={rectSortingStrategy}>
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{playlists.map((playlist) => (
						<SortablePlaylistCard key={playlist.id} playlist={playlist} />
					))}
				</div>
			</SortableContext>
			<DragOverlay dropAnimation={null}>
				{activePlaylist ? (
					<PlaylistCard
						playlist={activePlaylist}
						isDragOverlay
						style={activeWidth ? { width: activeWidth } : undefined}
					/>
				) : null}
			</DragOverlay>
		</DndContext>
	)
}

export function PlaylistGrid() {
	return (
		<Suspense fallback={<PlaylistGridSkeleton />}>
			<PlaylistGridContent />
		</Suspense>
	)
}
